import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import SearchBox from '../components/SearchBox';
import LoadingState from '../components/LoadingState';
import ChatBox from '../components/ChatBox';
import JobCard from '../components/JobCard';
import SEO, { serviceListSchema } from '../components/SEO';
import { SERVICES } from '../data/siteContent';
import { searchJobs, searchJobList, parseSearchText, resetParseContext } from '../services/api';
import './HomePage.css';

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const userTextsRef = useRef([]);
  const cvFileRef = useRef(null);
  // SearchJobs bağlamı: son yanıtın profile'ı bir sonraki çağrıda geri gönderilir
  const profileRef = useRef(null);
  const experienceAskedRef = useRef(false);
  const legacyModeRef = useRef(false);

  const performSearch = async (params) => {
    setIsLoading(true);
    setChatMode(false);
    setChatMessages(prev => prev.filter(m => !m.typing));

    try {
      const response = await searchJobList(params);
      await new Promise(resolve => setTimeout(resolve, 2500));
      const jobs = response.items || [];
      setResults(jobs.length > 0 ? jobs : []);
    } catch {
      await new Promise(resolve => setTimeout(resolve, 2500));
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // API'den dönen parse sonucunu işle
  const handleParseResult = async (parsed, userQuery) => {
    const data = parsed.data || parsed;
    const isReady = data.isReady === true;
    const aiMessage = parsed.clarifyQuestion || data.message || (!parsed.status && parsed.message) || null;

    // isReady false + AI mesaj var → ChatBox aç
    if (!isReady && aiMessage) {
      setChatMode(true);

      if (chatMessages.length === 0 && userQuery) {
        setChatMessages([
          { role: 'user', text: userQuery },
          { role: 'ai', text: aiMessage },
        ]);
      } else {
        setChatMessages(prev => [
          ...prev.filter(m => !m.typing),
          { role: 'ai', text: aiMessage },
        ]);
      }
      return;
    }

    // isReady=true veya arama verisi hazır → SearchJobList'e gönder
    const searchParams = {
      searchText: data.searchText ?? null,
      title: data.title ?? null,
      currentLocation: data.currentLocation ?? data.location ?? null,
      workingType: data.workingType ?? null,
      experienceYear: data.experienceYear ?? null,
      educationLevelId: data.educationLevelId ?? null,
      isMillitaryCompleted: data.isMillitaryCompleted ?? null,
      age: data.age ?? null,
    };

    const fullQuery = userTextsRef.current.join(', ');
    setSearchedQuery(fullQuery);
    setQuery(fullQuery);

    await performSearch(searchParams);
  };

  // SearchJobs yanıtını işle: eksik bilgi varsa sohbet, hazırsa ilanlar
  const handleSearchJobsResult = (res, userQuery) => {
    if (res.profile) profileRef.current = res.profile;

    if (!res.isReady) {
      const message = res.message || 'Aramanı biraz daha netleştirir misin?';
      if (/tecrübe|deneyim|yıl/i.test(message)) experienceAskedRef.current = true;

      setChatMode(true);
      setChatMessages((prev) => {
        const cleaned = prev.filter((m) => !m.typing);
        if (cleaned.length === 0 && userQuery) {
          return [{ role: 'user', text: userQuery }, { role: 'ai', text: message }];
        }
        return [...cleaned, { role: 'ai', text: message }];
      });
      return;
    }

    setChatMode(false);
    setChatMessages((prev) => prev.filter((m) => !m.typing));
    setSearchedQuery(userTextsRef.current.join(', '));
    setResults(res.jobs);
  };

  const handleSearch = async ({ query: q, cvFile: cv }) => {
    if (isLoading) return;

    setResults(null);
    setChatMode(false);
    setChatMessages([]);
    setSearchedQuery(q);
    userTextsRef.current = [q];
    cvFileRef.current = cv || null;
    profileRef.current = null;
    experienceAskedRef.current = false;
    legacyModeRef.current = false;
    resetParseContext();

    // API saniyeler sürebiliyor; yükleniyor durumu buradan başlamazsa
    // buton aktif kalıyor ve ekranda hiçbir şey olmuyor
    setIsLoading(true);

    try {
      const res = await searchJobs({ searchText: q, cv });
      handleSearchJobsResult(res, q);
    } catch (e) {
      if (e.code === 'UNAVAILABLE') {
        // Yeni uç erişilemiyor → eski iki adımlı akışa düş
        legacyModeRef.current = true;
        try {
          const parsed = await parseSearchText(q, cv);
          await handleParseResult(parsed, q);
        } catch {
          await performSearch({ searchText: q });
        }
      } else {
        await performSearch({ searchText: q });
      }
    } finally {
      // Sohbet moduna geçildiyse performSearch hiç çalışmaz, burada kapanmalı
      setIsLoading(false);
    }
  };

  const handleChatReply = async (message) => {
    setChatMessages(prev => [...prev, { role: 'user', text: message }]);
    userTextsRef.current.push(message);

    // Typing göster
    setChatMessages(prev => [...prev, { role: 'ai', text: '', typing: true }]);

    try {
      if (legacyModeRef.current) {
        // Eski akış: bağlam yok, tüm mesajlar birleştirilip gönderilir
        const fullText = userTextsRef.current.join(', ');
        const parsed = await parseSearchText(fullText, cvFileRef.current);
        await handleParseResult(parsed, null);
        return;
      }

      // Yeni akış: bağlam profile ile taşınır, yalnızca yeni mesaj gönderilir
      const res = await searchJobs({
        searchText: message,
        profile: profileRef.current,
        experienceAsked: experienceAskedRef.current,
      });
      handleSearchJobsResult(res, null);
    } catch (e) {
      if (e.code === 'UNAVAILABLE') {
        legacyModeRef.current = true;
        try {
          const parsed = await parseSearchText(userTextsRef.current.join(', '), cvFileRef.current);
          await handleParseResult(parsed, null);
          return;
        } catch { /* aşağıdaki hata mesajına düş */ }
      }
      setChatMessages(prev => [
        ...prev.filter(m => !m.typing),
        { role: 'ai', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' },
      ]);
    }
  };

  const handleChatClose = () => {
    setChatMode(false);
  };

  return (
    <div className="home">
      <SEO
        title="Yapay Zeka ile İş Bul"
        description="Ücretsiz ATS uyumlu CV oluştur, yapay zeka senin adına iş ilanlarına başvursun. Uzaktan çalışma ve yurt dışı fırsatları, dil engelini kaldıran toplantı çevirmeni, ABD ve Kanada çalışma yolu değerlendirmesi — hepsi isBulsun.com'da."
        keywords="ücretsiz cv oluştur, ATS uyumlu cv, yapay zeka iş bulma, otomatik iş başvurusu, iş ilanları, remote iş ilanları, uzaktan çalışma, yurt dışında çalışmak, dövizle kazanç, yabancı dil olmadan yurt dışı iş, ABD'de çalışmak, Kanada'da çalışmak, çalışma vizesi, iş ilanı uyum analizi, cv analizi, mülakat pratiği"
        path="/"
        structuredData={serviceListSchema(SERVICES)}
      />
      <section className={`hero ${results || isLoading ? 'hero--compact' : ''}`}>
        <div className="hero__bg"></div>

        <div className="hero__container">
          {!results && !isLoading && (
            <>
              <h1 className="hero__title">
                Yapay Zeka{' '}
                <span className="hero__title-gradient">işBulsun</span>
                <span className="hero__title-domain">.com</span>
              </h1>

              <p className="hero__subtitle">
                Nasıl bir iş aradığını, bulunduğun ili ve tecrübeni yaz sana uygun işleri Yapay Zeka bulsun.
              </p>
            </>
          )}

          <SearchBox
            onSearch={handleSearch}
            isLoading={isLoading}
            query={query}
            onQueryChange={setQuery}
            cvFile={cvFile}
            onCvChange={setCvFile}
          />

          {isLoading && <LoadingState />}
        </div>
      </section>

      {!results && !isLoading && !chatMode && (
        <section className="home-seo">
          <div className="home-seo__container">
            <h2 className="home-seo__title">
              Yapay zeka ile iş bul, ücretsiz ATS uyumlu CV oluştur
            </h2>
            <p className="home-seo__text">
              isBulsun.com; iş ilanı aramayı, özgeçmiş hazırlamayı ve başvuru yapmayı tek
              yerde toplar. Yukarıdaki kutuya nasıl bir iş aradığınızı yazmanız yeterli:
              yapay zeka ilanları sizin için tarar. Üye olduğunuzda ücretsiz olarak{' '}
              <strong>ATS uyumlu CV</strong> hazırlayabilir, ilan uyum analizinizi görebilir
              ve <strong>otomatik başvuru</strong> ile ilanlara sizin adınıza başvurulmasını
              sağlayabilirsiniz.
            </p>
            <p className="home-seo__text">
              Yurt dışında çalışmak isteyenler için <strong>uzaktan (remote)</strong>{' '}
              pozisyonlar, dil engelini kaldıran anlık toplantı çevirisi ile{' '}
              <strong>ABD ve Kanada</strong> çalışma yolu değerlendirmesi de üyeliğe dahildir.
            </p>
            <ul className="home-seo__links">
              {SERVICES.map((srv, i) => (
                <li key={i}>
                  <Link to="/nasil-calisir">{srv.name}</Link>
                </li>
              ))}
            </ul>
            <Link to="/nasil-calisir" className="home-seo__more">
              Tüm hizmetleri ve sık sorulan soruları gör →
            </Link>
          </div>
        </section>
      )}

      {chatMode && (
        <ChatBox
          messages={chatMessages}
          onSend={handleChatReply}
          onClose={handleChatClose}
        />
      )}

      {results && !isLoading && (
        <section className="results">
          <div className="results__container">
            <div className="results__header">
              <p className="results__count">
                <strong>{results.length}</strong> ilan bulundu
                {searchedQuery && <span className="results__query"> — "{searchedQuery}"</span>}
              </p>
            </div>

            {results.length > 0 ? (
              <div className="results__grid">
                {results.map((job, index) => (
                  <JobCard key={job.id || index} job={job} />
                ))}
              </div>
            ) : (
              <div className="results__empty">
                <p>Aramanızla eşleşen ilan bulunamadı. Farklı terimlerle tekrar deneyin.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
